import Annotation from '@zxy-cloud/react-image-annotation';
import React from 'react';
import { ITranslateProps, translate } from '../../../mixins/translate';
import { fetchJsonWithThrow } from '../../../store/fetch';
import * as conditionHelper from '../../../utils/conditionHelper';
import { craConditionToColorCode } from '../../../utils/craConditionHelper';
import { AssetTypes as assetTypes } from '../../../utils/enums/AssetTypes';
import { getAssetThumbnailUrl } from '../../../utils/getAssetThumbnailUrl';
import { ramsWeightedPriorityToColorCode } from '../../../utils/ramsWeightedPriorityToColorCode';
import { PanelRouterLink, Topbar, TopbarCrumbActive, TopbarLeft, TopbarRight } from '../../elements';
import { ElementSelection } from '../../Projects/ElementSelection';
import { Component } from '../../utils/Component';
import { showAssetInNewWindow } from '../../utils/showAssetInNewWindow';
import { SurveySelection } from '../Common/SurveySelection';

interface ISurveyEditImageAnnotationProps extends ITranslateProps {
    location: {
        query: {
            surveyid: string;
            objectid: string;
            returnUrl: string;
        };
    };
    match: { params: any };
}

interface IAnnotationData {
    craConditionScore?: number;
    id: string;
    text: string;
    categoryId?: string;
    elementId: string;
    unitId: string;
    manifestationId: string;
    conditionAdvice: string;
    conditionScore: string;
    assetId: string;
    assetName: string;
    assetType: assetTypes;
    failureModeId?: string;
    failureMode?:
        | {
              id: string;
              ramsWeightedPriority: null | string;
          }
        | undefined;
    surveyId?: string;
}

interface IAnnotationGeometry {
    height: number;
    width: number;
    x: number;
    y: number;
    type: string;
}

interface IAnnotation {
    geometry?: undefined | IAnnotationGeometry;
    data?: undefined | IAnnotationData;
}

interface ISurveyEditImageAnnotationState {
    annotations?: undefined | IAnnotation[];
    annotation?: undefined | IAnnotation;
    objectId?: undefined | string;
    surveyId?: undefined | string;
    assetId: string;
    submitting: boolean;
}

class SurveyEditImageAnnotation extends Component<ISurveyEditImageAnnotationProps, ISurveyEditImageAnnotationState> {
    constructor(props: ISurveyEditImageAnnotationProps) {
        super(props);

        this.state = {
            annotation: {},
            annotations: [],
            submitting: false,
            assetId: this.props.match.params.id,
            surveyId: this.props.location.query.surveyid !== 'undefined' ? this.props.location.query.surveyid : undefined,
            objectId: this.props.location.query.objectid !== 'undefined' ? this.props.location.query.objectid : undefined,
        };
    }

    public override async componentDidMount() {
        await this.load();
    }

    public override render() {
        return (
            <>
                <Topbar>
                    <TopbarLeft>
                        <TopbarCrumbActive title={this.props.t('common.editItem', this.props.t('common.annotations'))} icon="fa-tag" />
                    </TopbarLeft>
                    <TopbarRight>
                        <PanelRouterLink
                            to={this.props.location.query.returnUrl}
                            text={this.props.t('survey.returnEditInspection')}
                            icon="fa fa-angle-double-left"
                            small={true}
                        />
                    </TopbarRight>
                </Topbar>

                <div className="pr50">
                    <Annotation
                        src={`/api/assets/${this.state.assetId}/download`}
                        style={{ imageOrientation: 'from-image' }}
                        annotations={this.state.annotations}
                        disableAnnotation={this.state.submitting}
                        value={this.state.annotation}
                        renderEditor={(props: any) => this.renderEditor({ ...props })}
                        renderContent={(props: any) => this.renderContent(props)}
                        renderHighlight={(props: any) => this.renderHighlight(props)}
                        renderOverlay={() => <div />}
                        onChange={async (annotation: any) => this.onChange(annotation)}
                        onSubmit={async (annotation: any) => this.create(annotation)}
                    />
                </div>
            </>
        );
    }

    private async onChange(annotation: IAnnotation) {
        await this.setState({ annotation });
    }

    private renderContent({ annotation }: any) {
        const { geometry } = annotation as { geometry: IAnnotationGeometry };
        if (annotation.data) {
            return (
                <div
                    key={annotation.data.id}
                    style={{
                        background: 'black',
                        color: 'white',
                        padding: 10,
                        position: 'absolute',
                        fontSize: 12,
                        left: `${geometry.x}%`,
                        top: `${geometry.y + geometry.height}%`,
                    }}
                >
                    {annotation.data.assetId && (
                        <div>
                            <a href="#" onClick={(e) => this.onShowAsset(e, annotation.data as IAnnotationData)}>
                                <img
                                    src={getAssetThumbnailUrl({
                                        id: (annotation.data as IAnnotationData).assetId,
                                        type: (annotation.data as IAnnotationData).assetType,
                                    })}
                                    className={'img-responsive'}
                                    title={annotation.data.name}
                                    width="240px"
                                    style={{ marginLeft: 'auto', marginRight: 'auto' }}
                                />
                            </a>
                        </div>
                    )}
                    <div style={{ maxWidth: '260px' }}>{annotation.data && annotation.data.conditionAdvice}</div>
                    <div style={{ float: 'right' }}>
                        <button
                            type="button"
                            className="btn btn-xs btn-default fw600 mt5 fs12"
                            onClick={() => {
                                void this.remove(annotation.data.id);
                            }}
                            title={this.props.t('common.remove')}
                        >
                            <span className="fa fa-trash-o"></span>
                        </button>
                    </div>
                </div>
            );
        }
        return null;
    }

    private renderEditor(props: any) {
        const { geometry } = props.annotation as { geometry: IAnnotationGeometry };
        if (!geometry) {
            return null;
        }

        function onElementSelectionChange(value: any) {
            let text = (props.annotation.data ? props.annotation.data.text : '') || '';
            if (!props.annotation.ignoreTitleOverride) {
                if (value.assetName) {
                    text = value.assetName;
                } else if (value.failureModeName) {
                    text = value.failureModeName;
                } else if (value.manifestationName) {
                    text = value.manifestationName;
                } else if (value.unitName) {
                    text = value.unitName;
                } else if (value.elementName) {
                    text = value.elementName;
                }
            }
            props.onChange({ ...props.annotation, data: { ...props.annotation.data, ...value, text } });
        }

        function onElementSurveyChange(value: any) {
            const text = (props.annotation.data ? props.annotation.data.text : '') || '';
            props.onChange({ ...props.annotation, data: { ...props.annotation.data, ...{ surveyId: value }, text } });
        }

        return (
            <div
                style={{
                    background: 'rgba(255, 255, 255, 0.9)',
                    borderRadius: 3,
                    position: 'absolute',
                    left: `${geometry.x}%`,
                    top: `${geometry.y + geometry.height}%`,
                }}
            >
                <div style={{ padding: '10px' }}>
                    <div>
                        <input
                            className="mb10"
                            placeholder={this.props.t('common.title')}
                            value={(props.annotation.data ? props.annotation.data.text : '') || ''}
                            onChange={(e) =>
                                props.onChange({
                                    ...props.annotation,
                                    data: { ...props.annotation.data, text: e.target.value },
                                    ignoreTitleOverride: true,
                                })
                            }
                            style={{ width: '320px', padding: '5px 10px' }}
                        />
                    </div>

                    <div>
                        {this.state.surveyId && (
                            <ElementSelection
                                surveyId={this.state.surveyId}
                                onChange={(value) => onElementSelectionChange(value)}
                                elementId={props.annotation.data ? props.annotation.data.elementId : undefined}
                                unitId={props.annotation.data ? props.annotation.data.unitId : undefined}
                                manifestationId={props.annotation.data ? props.annotation.data.manifestationId : undefined}
                                failureModeId={props.annotation.data ? props.annotation.data.failureModeId : undefined}
                                assetId={props.annotation.data ? props.annotation.data.assetId : undefined}
                                useAssets={true}
                                useFailureModes={true}
                            />
                        )}
                        {!this.state.surveyId && this.state.objectId && (
                            <SurveySelection objectId={this.state.objectId} onChange={(value) => onElementSurveyChange(value)} />
                        )}
                    </div>

                    <div className="pull-right">
                        <button
                            type="button"
                            className="btn btn-sm btn-primary fw600 ml5 mt5 mb5 fs12"
                            onClick={props.onSubmit}
                            title={this.props.t('common.save')}
                        >
                            <span className="fa fa-save"></span>
                        </button>
                    </div>
                </div>
            </div>
        );
    }

    // eslint-disable-next-line @typescript-eslint/naming-convention
    private renderHighlight({ annotation, active }: { annotation: IAnnotation; active: boolean }) {
        const { geometry, data } = annotation;

        if (!geometry) {
            return null;
        }

        let borderColor = '#f500e4';
        if (data?.failureMode?.ramsWeightedPriority) {
            borderColor = ramsWeightedPriorityToColorCode(data?.failureMode.ramsWeightedPriority);
        } else if (data?.conditionScore) {
            borderColor = conditionHelper.codeToColorCode(data.conditionScore, undefined);
        } else if (annotation?.data?.craConditionScore) {
            borderColor = craConditionToColorCode(annotation.data.craConditionScore);
        }

        return (
            <div
                key={data?.id ?? '1'}
                style={{
                    fontSize: '13px',
                    border: `dashed 3px ${borderColor}`,
                    background: active ? 'rgba(200, 200, 200, 0.0)' : 'rgba(200, 200, 200, 0.5)',
                    position: 'absolute',
                    left: `${geometry.x}%`,
                    top: `${geometry.y}%`,
                    height: `${geometry.height}%`,
                    width: `${geometry.width}%`,
                }}
            >
                {data?.text && data.text !== '' && (
                    <span
                        style={{
                            color: borderColor,
                            fontWeight: 'bold',
                            whiteSpace: 'nowrap',
                            padding: '2px',
                            backgroundColor: 'rgba(255, 255, 255, 0.8)',
                        }}
                    >
                        {data.text}
                    </span>
                )}
            </div>
        );
    }

    private onShowAsset(e: React.MouseEvent<any>, annotation: IAnnotationData) {
        e.preventDefault();

        if (this.state.surveyId && this.state.surveyId !== 'undefined') {
            showAssetInNewWindow({ id: annotation.assetId, type: annotation.assetType }, { surveyId: this.state.surveyId });
        } else {
            showAssetInNewWindow({ id: annotation.assetId, type: annotation.assetType });
        }
    }

    private async create(annotation: any) {
        const { geometry, data } = annotation;

        if (data) {
            const observationPoint = {
                isSourcePoint2d: true,
                source2dAssetId: this.state.assetId,
                geometry2d: geometry,
                title: data.text,
                elementId: data.elementId,
                unitId: data.unitId,
                manifestationId: data.manifestationId,
                failureModeId: data.failureModeId,
                assetId: data.assetId,
                targetSurveyId: data.surveyId,
            } as any;

            let url: string;
            if (this.state.surveyId) {
                url = `/api/surveys/${this.state.surveyId}/observationPoints/`;
            } else {
                url = `/api/objects/${this.state.objectId}/observationPoints`; // annotation available in object
            }

            await this.setState({ submitting: true });
            await fetchJsonWithThrow(url, 'POST', observationPoint);

            await this.load();
        }
        await this.setState({
            submitting: false,
            annotation: {},
        });
    }

    private async load() {
        const annotations: IAnnotation[] = [];
        let url = '';
        if (this.state.surveyId) {
            url = `/api/surveys/${this.state.surveyId}/observationPoints?is2dsource=true&twodsourceassetid=${this.state.assetId}`; // annotation available in survey
        } else if (this.state.objectId) {
            url = `/api/objects/${this.state.objectId}/observationPoints?is2dsource=true&twodsourceassetid=${this.state.assetId}`; // annotation available in object
        }

        if (url !== '') {
            await this.setState({ submitting: true });
            const result = await fetchJsonWithThrow(url, 'GET');

            result.json.map((item: any) => {
                const annotation: IAnnotation = {
                    geometry: item['geometry2d'],
                    data: {
                        id: item.id,
                        text: item.title,
                        elementId: item.elementId,
                        unitId: item.unitId,
                        manifestationId: item.manifestationId,
                        assetId: item.assetId,
                        assetName: item.assetName,
                        assetType: item.assetType,
                        conditionAdvice:
                            item.description || item.elementConditionAdvice || item.unitConditionAdvice || item.manifestationConditionAdvice,
                        conditionScore: item.elementConditionScore || item.unitConditionScore || item.manifestationConditionScore,
                        failureMode: item.failureModeId
                            ? {
                                  id: item.failureModeId,
                                  ramsWeightedPriority:
                                      item.failureModeVerificationRamsWeightedPriority || item.failureModeAnalysisRamsWeightedPriority,
                              }
                            : undefined,
                    },
                };
                annotations.push(annotation);
            });
        }

        await this.setState({
            submitting: false,
            annotation: {},
            annotations,
        });
    }

    private async remove(annotationId: string) {
        let url: string;
        if (this.state.surveyId) {
            url = `/api/surveys/${this.state.surveyId}/observationPoints/${annotationId}`;
        } else {
            url = `/api/objects/${this.state.objectId}/observationPoints/${annotationId}`;
        }

        await this.setState({ submitting: true });
        await fetchJsonWithThrow(url, 'DELETE');
        await this.setState({ annotation: {}, submitting: false });
        await this.load();
    }
}

// eslint-disable-next-line import/no-default-export
export default translate()(SurveyEditImageAnnotation);
